import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertTitle } from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
import type { CellValueChangedEvent } from 'ag-grid-community/dist/types/core/events';
import type { ProcessCellForExportParams } from 'ag-grid-community/dist/types/core/interfaces/exportParams';
import { round2 } from '@callmart/types';
import TableGridItem from '../UI/TableGridItem';
import { BudgetSummaryMonth } from '../../services/budgetBillingConfig';
import { OnTopCostWithRelations } from '../../services/budgetOnTopCost';
import { useBudgetSummary, useBillingConfigs } from '../../hooks/api/budgetBillingConfig';
import { PendingCellEdits } from '../../hooks/usePendingCellEdits';
import { useBudgetContext, useBudgetWindow } from '../../store/Budget/BudgetContext';
import { calculateAllCosts, calculateMonthlyCostTotals } from './budgetCalculation';
import {
    generateMonthColumns,
    getContiguousMonths,
    formatEuroOptionalCents,
    formatMonthRange,
    formatWholeEuro,
    wholeEuroValue,
    isBudgetRangeActive,
    parseBudgetCell,
} from './helpers';
import { useOverviewMonths } from './useOverviewMonths';
import { pendingBorder } from './pendingCellStyle';
import { BASELINE_EDIT_KIND, BASELINE_ROW_ID } from './overviewEditPayloads';

interface BudgetSummaryProps {
    versionId: number;
    onTopCosts: OnTopCostWithRelations[];
    /** The Overview's pending edits — the Ist-Budget row records into them (CALL-779). */
    pending: PendingCellEdits;
    // CALL-752: when false, the Ist Budget shows real entered-rate costs only (no forward-extrapolation,
    // no blue). Driven by the "Mit Extrapolation" checkbox in BudgetOverview; defaults to on.
    showExtrapolation?: boolean;
    /** CALL-781: hide month COLUMNS before the current month. Display only — the pinned total and the row
     *  values keep coming from the full budget period. */
    hidePastMonths?: boolean;
}

interface SummaryRow {
    label: string;
    rowKey: string;
    [monthKey: string]: unknown;
}

// CALL-760: `label` is a semantic i18n key resolved via t(label) in the budget namespace.
const ROW_DEFS = [
    { key: 'budget', label: 'summary.budget' },
    { key: 'totalCost', label: 'summary.totalCost' },
    // CALL-756: indented sub-rows that break "Kosten gesamt" into its two parts
    // (handlingCost + onTopCost === totalCost) so the make-up of the total is visible.
    { key: 'handlingCost', label: 'summary.handlingCost' },
    { key: 'onTopCost', label: 'summary.onTopCost' },
    { key: 'delta', label: 'summary.delta' },
    { key: 'onTopRisks', label: 'summary.opportunitiesRisks' },
    { key: 'deltaInclRisks', label: 'summary.deltaInclRisks' },
];

// CALL-756: the breakdown children render indented + non-bold under the bold "Kosten gesamt".
const SUB_ROW_KEYS = new Set(['handlingCost', 'onTopCost']);

/**
 * CALL-813: euro display for every cell on this grid, month columns and the per-year total alike.
 *
 * The Ist-Budget row is the only one a person types, and they type whole thousands — so it shows
 * cents only when someone actually entered some, rather than printing a ",00" on every cell. Its
 * year total follows the same rule: it is the sum of those same typed figures, so rounding it while
 * a month cell shows ",43" would contradict the row's own data. Every other row is a computed sum
 * and rounds, which is what the ticket asks for.
 *
 * Keyed on the row, not on `isReadOnly`, so a snapshot and its draft read identically.
 *
 * Rounding is per cell and DISPLAY ONLY, so the figures on screen do not foot exactly: two sub-rows of
 * 100,50 read "101 €" each under a parent of "201 €", Delta can sit 1 € off the rows it is derived
 * from, and the year total is round(sum) rather than the sum of the twelve rounded months it sits
 * beside. Underlying values are untouched, and the budget is entered in whole thousands, so in practice
 * only the cost rows carry cents at all. Reconciling on screen would mean rounding every row to the
 * same precision — which is what dropping the Ist-Budget row's cents would buy, and what the client
 * decided against.
 */
const euroByRow = (params: { data?: { rowKey?: string }; value: unknown }): string =>
    params.data?.rowKey === 'budget' ? formatEuroOptionalCents(params.value) : formatWholeEuro(params.value);

/** The whole-euro figure a Delta cell actually SHOWS, 0 where it shows nothing. Colour rules read this
 *  rather than the raw value: `formatWholeEuro` normalises a rounded-zero to 0, so a Delta of -0,40
 *  displays "0 €" and reading the raw −0.4 painted that zero red (and +0,40 painted it green). It comes
 *  from the formatter's own helper so the colour cannot disagree with the digits. */
const displayedWholeEuro = (value: unknown): number => wholeEuroValue(value) ?? 0;

const BudgetSummary = ({
    versionId,
    onTopCosts,
    pending,
    showExtrapolation = true,
    hidePastMonths = false,
}: BudgetSummaryProps) => {
    const { t } = useTranslation('budget');
    const { data: summaryData = [], isLoading, isError, error } = useBudgetSummary(versionId);

    // CALL-779: the Ist-Budget row's pending months. Applied to the row below AND to everything derived
    // from it — Delta is budget minus cost, so an unsaved baseline moves it immediately instead of only
    // after a save/refetch.
    const pendingBaselines = useMemo(() => {
        const byMonth = new Map<string, number | null>();
        for (const edit of pending.edits) {
            if (edit.kind === BASELINE_EDIT_KIND) byMonth.set(edit.key, edit.value as number | null);
        }
        return byMonth;
    }, [pending.edits]);
    const [configs] = useBillingConfigs(versionId);
    const calculated = useMemo(
        () => calculateAllCosts(configs, { extrapolate: showExtrapolation }),
        [configs, showExtrapolation]
    );
    const handlingCostTotals = useMemo(() => calculateMonthlyCostTotals(calculated), [calculated]);
    // CALL-752: months whose handling cost is (partly) driven by a forward-extrapolated rate, so the
    // Kosten gesamt cell can be painted blue (#e3f2fd) like the per-unit Kosten grid and Strat FC.
    const extrapolatedTotalMonths = useMemo(() => {
        const set = new Set<string>();
        for (const { monthlyResults } of calculated) {
            for (const [month, row] of monthlyResults) {
                if (row.extrapolated) set.add(month);
            }
        }
        return set;
    }, [calculated]);
    const { isReadOnly, isSnapshot } = useBudgetContext();
    const { windowStart, rangeFrom, rangeTo } = useBudgetWindow();
    // CALL-752: a snapshot's summary is the immutable frozen record from the API — always read those
    // backend totals, never recompute client-side (stale/failed config or on-top queries must not
    // change a published budget). A draft uses the backend (extrapolated) totals only when the toggle
    // is on; with it off it recomputes entered-only, since the draft summary endpoint always extrapolates.
    const useBackendTotals = isSnapshot || showExtrapolation;
    const gridRef = useRef<AgGridReact>(null);

    // CALL-749: the budget period is the span the user actually budgeted — the months returned by
    // the (now period-bounded) summary endpoint plus any non-zero included on-top months. We must
    // NOT widen it with handlingCostTotals.keys(): those come from the global config prices joined
    // with auto-populated forecast volumes, which reach back to 2024 and would drag the grid (and
    // the per-year total columns) back to 2024 even though no budget was entered there.
    const dataMonths = useMemo(() => {
        const set = new Set<string>();
        for (const m of summaryData as BudgetSummaryMonth[]) set.add(m.month);
        for (const cost of onTopCosts) {
            if (!cost.includeInBudget) continue;
            for (const value of cost.values || []) {
                if (Number(value.amount ?? 0) !== 0) set.add(value.month);
            }
        }
        // CALL-779: a month the user has just typed a budget into is part of the period too. rowData
        // below populates month keys from this list only, so without it an entry outside the stored
        // period (the window navigates freely) was recorded as pending and then rendered blank — the
        // figure vanished from the grid while the save bar counted it as an unsaved change.
        for (const month of pendingBaselines.keys()) set.add(month);
        return Array.from(set);
    }, [summaryData, onTopCosts, pendingBaselines]);

    // Full budget period drives the row data and the per-year total columns; the scrolling window
    // only controls which month columns are visible (shared with the other Overview grids). It's the
    // CONTIGUOUS span of the budgeted months — no full-calendar-year padding, so entering a single
    // month (e.g. Dez 2025) doesn't fill the rest of that year with empty 0 columns.
    const fullMonths = useMemo(() => getContiguousMonths(dataMonths), [dataMonths]);
    // CALL-781: `visibleMonths` narrows the COLUMNS only. The rows and the pinned total are built from
    // fullMonths + `months` below, so a hidden month still counts toward the total's figure.
    // CALL-815: `months` is the 12-month window, or the selected date range when one is set.
    const { months, visibleMonths } = useOverviewMonths(hidePastMonths);

    const monthColumns = useMemo(
        () =>
            generateMonthColumns(visibleMonths).map((col) => ({
                ...col,
                editable: (params: any) => params.data?.rowKey === 'budget' && !isReadOnly,
                valueParser: (params: any) => parseBudgetCell(params.newValue),
                valueFormatter: euroByRow,
                cellStyle: (params: any): Record<string, string> => {
                    // Month numbers are right-aligned; this callback REPLACES generateMonthColumns'
                    // default { textAlign: 'right' }, so carry it through every branch.
                    const style: Record<string, string> = { textAlign: 'right' };
                    const rowKey = params.data?.rowKey;
                    const field = params.colDef?.field as string | undefined;
                    if (rowKey === 'budget' && !isReadOnly) {
                        return {
                            ...style,
                            backgroundColor: '#fffde7',
                            ...pendingBorder(Boolean(field && pendingBaselines.has(field))),
                        };
                    }
                    // CALL-752: a Kosten gesamt month driven by an extrapolated rate is light blue.
                    // ALWAYS set an explicit backgroundColor for this row (blue or reset) — omitting it
                    // leaves ag-grid's previously-applied blue in place when the month flips back to
                    // entered, so the cell would stay stale blue after the rate is committed.
                    // CALL-756: the handling sub-row carries the same extrapolation tint as the total.
                    if ((rowKey === 'totalCost' || rowKey === 'handlingCost') && field) {
                        return { ...style, backgroundColor: extrapolatedTotalMonths.has(field) ? '#e3f2fd' : '' };
                    }
                    if (rowKey === 'delta' || rowKey === 'deltaInclRisks') {
                        const val = displayedWholeEuro(params.value);
                        if (val > 0) return { ...style, color: '#2e7d32' };
                        if (val < 0) return { ...style, color: '#d32f2f' };
                    }
                    return style;
                },
            })),
        [visibleMonths, isReadOnly, extrapolatedTotalMonths, pendingBaselines]
    );

    // CALL-772: the pinned total covers exactly the months the view holds, and is labeled with them, so
    // it changes as you navigate — instead of one total across the whole multi-year period. That was the
    // calendar year the Jan-aligned window sits on; since CALL-815 it is the `Datum von`–`bis` range
    // whenever one is set. `months` IS that list in both cases, so one Set answers for both.
    //
    // The pinned total, NOT the row values: an off-view month keeps its figure on the row (the export and
    // the save need the whole period) — it simply is not part of the total the user is looking at.
    const totalMonths = useMemo(() => new Set(months), [months]);
    const totalHeader = isBudgetRangeActive(rangeFrom, rangeTo)
        ? formatMonthRange(rangeFrom, rangeTo)
        : windowStart.slice(0, 4);

    const sumColumn: ColDef = useMemo(
        () => ({
            headerName: totalHeader,
            field: '__sum',
            minWidth: 130,
            editable: false,
            pinned: 'right',
            type: 'numericColumn',
            cellStyle: (params: any): Record<string, string> => {
                const base: Record<string, string> = { fontWeight: 'bold', textAlign: 'right' };
                const rowKey = params.data?.rowKey;
                if (rowKey === 'delta' || rowKey === 'deltaInclRisks') {
                    const val = displayedWholeEuro(params.value);
                    if (val > 0) base.color = '#2e7d32';
                    if (val < 0) base.color = '#d32f2f';
                }
                return base;
            },
            valueFormatter: euroByRow,
        }),
        [totalHeader]
    );

    const columnDefs = useMemo<ColDef[]>(
        () => [
            {
                headerName: '',
                field: 'label',
                minWidth: 250,
                pinned: 'left',
                editable: false,
                cellStyle: (params: any): Record<string, string> =>
                    SUB_ROW_KEYS.has(params.data?.rowKey)
                        ? { fontWeight: 'normal', paddingLeft: '24px' }
                        : { fontWeight: 'bold' },
            },
            ...monthColumns,
            sumColumn,
        ],
        [monthColumns, sumColumn]
    );

    const rowData = useMemo<SummaryRow[]>(() => {
        const byMonth = new Map((summaryData as BudgetSummaryMonth[]).map((m) => [m.month, m]));
        const onTopIncluded = new Map<string, number>();

        for (const cost of onTopCosts) {
            if (!cost.includeInBudget) continue;
            for (const value of cost.values || []) {
                onTopIncluded.set(value.month, (onTopIncluded.get(value.month) ?? 0) + Number(value.amount ?? 0));
            }
        }

        const valueFor = (monthKey: string, key: string): number => {
            const monthData = byMonth.get(monthKey);
            const pendingBudget = pendingBaselines.get(monthKey);
            const budget = pendingBudget !== undefined ? (pendingBudget ?? 0) : (monthData?.budget ?? 0);
            const handlingCost = handlingCostTotals.get(monthKey) ?? 0;
            const includedCost = onTopIncluded.get(monthKey) ?? 0;
            const riskCost = monthData?.onTopRisks ?? 0;
            const localTotalCost = handlingCost + includedCost;
            const localDelta = budget - localTotalCost;

            // CALL-752: a snapshot reads the immutable frozen backend totals (useBackendTotals). For a
            // draft: extrapolation on → prefer the backend (extrapolated) totals; off → recompute the
            // entered-rate totals locally, since the draft summary endpoint always extrapolates.
            const totalCost = useBackendTotals ? (monthData?.totalCost ?? localTotalCost) : localTotalCost;
            // CALL-779: a month with an unsaved baseline takes its Delta from THAT budget and the cost
            // actually on screen. The server's delta was computed from the stored baseline, so with
            // extrapolation on (the default) it won a month the user had just retyped and the row read
            // as Budget − Kosten ≠ Delta until the save came back.
            const pendingDelta = pendingBudget !== undefined ? budget - totalCost : null;
            const delta = pendingDelta ?? (useBackendTotals ? (monthData?.delta ?? localDelta) : localDelta);
            const deltaInclRisks =
                pendingDelta !== null
                    ? pendingDelta - riskCost
                    : useBackendTotals
                      ? (monthData?.deltaInclRisks ?? delta - riskCost)
                      : localDelta - riskCost;

            // CALL-756: break "Kosten gesamt" into its two parts so the sub-rows sum to the total.
            // They reconcile on the underlying values; since CALL-813 rounds each computed row for
            // display, two children of 100,50 can show 101 € each against a parent of 201 €.
            // When reading the backend summary (a snapshot, or a draft with
            // extrapolation on) take the split straight from it, so a still-loading / stale / failed
            // on-top query can't desync the sub-rows from the total; fall back to the local on-top
            // list. With a draft's extrapolation off the FE-local entered-rate values are authoritative.
            const onTopCost = useBackendTotals ? (monthData?.onTopIncluded ?? includedCost) : includedCost;
            const handlingCostValue = useBackendTotals
                ? (monthData?.handlingCost ?? totalCost - onTopCost)
                : handlingCost;

            const valueMap: Record<string, number> = {
                budget,
                totalCost,
                handlingCost: handlingCostValue,
                onTopCost,
                delta,
                onTopRisks: riskCost,
                deltaInclRisks,
            };

            return valueMap[key] ?? 0;
        };

        // Populate every full-period month (not just the visible window) so editing a budget cell
        // saves the complete baseline set — off-window months are never wiped.
        return ROW_DEFS.map(({ key, label }) => {
            const row: SummaryRow = { label: t(label), rowKey: key };
            // CALL-772/815: the pinned total (__sum) sums only the months the view holds — the viewed
            // calendar year, or the selected date range — so it follows the window and the range alike.
            let viewSum = 0;
            for (const monthKey of fullMonths) {
                const value = valueFor(monthKey, key);
                row[monthKey] = value;
                if (totalMonths.has(monthKey)) viewSum += value;
            }
            row.__sum = viewSum;
            return row;
        });
    }, [summaryData, fullMonths, totalMonths, handlingCostTotals, onTopCosts, useBackendTotals, pendingBaselines, t]);

    // CALL-752: ag-grid skips re-running cellStyle when a cell's value is unchanged — but a Kosten
    // gesamt month can flip extrapolated→entered without its total changing (e.g. accepting the
    // projected rate as-is), which would otherwise leave a stale blue background. Force a repaint
    // whenever the data or the extrapolated set changes.
    useEffect(() => {
        gridRef.current?.api?.refreshCells({ force: true });
    }, [rowData, extrapolatedTotalMonths]);

    // CALL-779: record the edited month for the page's Save. Only the Ist-Budget row is editable, so
    // one constant row id carries the whole series.
    const handleCellValueChanged = useCallback(
        (event: CellValueChangedEvent) => {
            const { data, colDef } = event;
            if (data?.rowKey !== 'budget' || !colDef.field || !/^\d{4}-\d{2}-\d{2}$/.test(colDef.field)) return;

            const month = colDef.field;
            const value = data[month] == null ? null : Number(data[month]);
            const served = (summaryData as BudgetSummaryMonth[]).find((entry) => entry.month === month)?.budget;
            pending.set(BASELINE_ROW_ID, BASELINE_EDIT_KIND, month, value, served);
        },
        [pending, summaryData]
    );

    if (isError) {
        return (
            <Alert severity="error">
                <AlertTitle>Failed to load budget summary</AlertTitle>
                <span>{error?.message || 'An unexpected error occurred. Please try again.'}</span>
            </Alert>
        );
    }

    return (
        <TableGridItem
            ref={gridRef}
            height="auto"
            columnDefs={columnDefs}
            rowData={rowData}
            loading={isLoading}
            domLayout="autoHeight"
            // CALL-779: the rows are a fixed set keyed by rowKey, and `rowData` now gets a new identity on
            // every committed cell (it reads the pending edits). Without this ag-grid rebuilt every
            // RowNode on each commit, which stops editing and drops focus — so tabbing along the
            // Ist-Budget row was impossible. Every other converted Budget grid already passes one.
            getRowId={(params) => String(params.data.rowKey)}
            onCellValueChanged={handleCellValueChanged}
            // CALL-813: the computed rows DISPLAY whole euros, but a copy has to keep the cents —
            // the ticket only rounds the presentation. useValueFormatterForExport is per column and
            // this column set mixes the editable Ist-Budget row with six computed ones, so the raw
            // value has to be recovered per cell here. Decimal comma so it pastes straight into a
            // German Excel, as in StrategicVolume and CapacityReportTable.
            // Numbers only: the first column holds the row LABEL, and a blanket replace turned
            // "Delta inkl. Chancen/Risiken" into "Delta inkl, Chancen/Risiken" whenever a copied
            // range included it.
            // round2, NOT whole euros: a copy keeps the cents, but the computed rows are running sums
            // of already-rounded cell costs (calculateMonthlyCostTotals accumulates without rounding),
            // so their raw float carries binary noise — 66,44 + 89,02 + 41,04 is 196.49999999999997,
            // which would land in Excel verbatim instead of as 196,50.
            processCellForClipboard={(params: ProcessCellForExportParams) => {
                if (params.value == null) return '';
                if (typeof params.value !== 'number') return String(params.value);
                return String(round2(params.value)).replace('.', ',');
            }}
            defaultColDef={{
                resizable: true,
                sortable: false,
                filter: false,
                suppressMenu: true,
            }}
            cellSelection
        />
    );
};

export default BudgetSummary;
